import React, { useRef, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Checkbox, TextArea } from '@ygtechnologies/bygr-frontend-component-library';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { rejectResidentialUnitDocument } from '@/services/documents';
import { Button } from '@/components/Buttons';
import DocumentResidentialUnitSelection from './DocumentResidentialUnitSelection';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';

interface OwnProps {
    documentVersionId: number;
    residentialUnitId: number | null;
    onClose(): void;
    onReject(residentialUnitIds: number[], params: { reason: string }): void;
    show: boolean;
    uploadedByName: string;
}

const DocumentRejectDialog: React.FC<OwnProps> = ({
    documentVersionId,
    residentialUnitId,
    onClose,
    onReject,
    show,
    uploadedByName,
}) => {
    const [reason, setReason] = useState('');
    const [saving, setSaving] = useState(false);
    const [sendRejectEmail, setSendRejectEmail] = useState(false);
    const [selectedResidentialUnitIds, setSelectedResidentialUnitIds] = useState<number[]>([]);
    const focusElementRef = useRef<HTMLTextAreaElement>(null);
    const projectId = useSelector((state: IState) => state.global.activeProject!.id);

    const handleOpen = () => {
        setReason('');
        setSaving(false);
        setSelectedResidentialUnitIds([]);

        if (focusElementRef.current) {
            focusElementRef.current.focus();
        }
    };

    const handleRejectClick = async (e) => {
        setSaving(true);

        try {
            await rejectResidentialUnitDocument(documentVersionId, selectedResidentialUnitIds, reason, sendRejectEmail);

            triggerHandler(onReject, selectedResidentialUnitIds, { reason });
            triggerHandler(onClose);
        } finally {
            setSaving(false);
        }
    };

    const handleCancelClick = () => {
        triggerHandler(onClose);
    };

    // const linkMap = new Set(links.map((x) => x.residentialUnitId));

    return (
        <Modal isOpen={show} toggle={handleCancelClick} onOpen={handleOpen}>
            <ModalHeader>
                <Trans>Reject document</Trans>
            </ModalHeader>
            <ModalBody>
                <div className="form-group">
                    <label>
                        <Trans>Reason</Trans>
                    </label>
                    <TextArea
                        ref={focusElementRef}
                        value={reason}
                        name="reason"
                        className="form-control"
                        placeholder={t`Reason`}
                        onChange={(e) => setReason(e.target.value)}
                    />
                </div>

                <DocumentResidentialUnitSelection
                    projectId={projectId}
                    documentVersionId={documentVersionId}
                    residentialUnitId={residentialUnitId}
                    onChange={setSelectedResidentialUnitIds}
                    // value={selectedResidentialUnitIds}
                    singleCaption={<Trans>Reject this unit only</Trans>}
                    allCaption={<Trans>Reject all relevant units</Trans>}
                    multiCaption={<Trans>Reject specific units</Trans>}
                />
                <Checkbox checked={sendRejectEmail} onChange={() => setSendRejectEmail(!sendRejectEmail)}>
                    <Trans>Send email notifying {uploadedByName} about rejecting this document.</Trans>
                </Checkbox>
            </ModalBody>
            <ModalFooter>
                <Button color="tertiary" onClick={handleCancelClick} disabled={saving}>
                    <Trans>Cancel</Trans>
                </Button>
                <Button color="danger" onClick={handleRejectClick} loading={saving}>
                    <Trans>Reject</Trans>
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default DocumentRejectDialog;